import type { WorkspaceAuditLogItem } from './types'

export type AuditLogTone = 'positive' | 'negative' | 'neutral'

type AuditItems = {
  [Item in WorkspaceAuditLogItem as Item['__typename']]: Item
}
type ToneClassifiers = {
  [Type in keyof AuditItems]?:
    | AuditLogTone
    | ((item: AuditItems[Type]) => AuditLogTone)
}

const availabilityTone = (enabled: boolean | null): AuditLogTone =>
  enabled == null ? 'neutral' : enabled ? 'positive' : 'negative'

// Colour describes additions/removals of access, resources or capabilities,
// not whether an administrator made a good or bad decision. Configuration
// changes with no known direction remain neutral. Only admin-facing events
// have classifiers; hidden or unrecognised events fall back to neutral.
const toneClassifiers: ToneClassifiers = {
  AcceptMembershipInviteAudit: 'positive',
  AddMemberPermissionGroup: 'positive',
  ArchiveProject: 'negative',
  CancelLicenseAudit: 'negative',
  ChangeAccountLinkingAudit: item => availabilityTone(item.enabled),
  ChangedPlanAudit: 'neutral', // The event does not identify an upgrade/downgrade.
  ChangeRestrictedUploadsAudit: item =>
    availabilityTone(!item.areUploadsRestricted),
  ChangeScimAutoLinkAudit: item => availabilityTone(item.enabled),
  ChangeSecurityEmailAudit: 'neutral',
  ConfigureEncryptionKeysAudit: 'neutral',
  CreateMembershipAudit: 'positive',
  CreatePermissionGroup: 'positive',
  CreatePermissionGroupRelation: 'positive',
  CreateWorkspaceAudit: 'positive',
  CreditAudit: 'positive',
  DeleteMembershipAudit: 'negative',
  DeleteOrganizationAudit: 'negative',
  DeletePermissionGroup: 'negative',
  DisableMembershipAudit: 'negative',
  DisableSsoAudit: 'negative',
  // Despite "downgraded" in the API name, this adds a new Viewer via SSO.
  DowngradedSsoMembershipProvisionedAudit: 'positive',
  InstallIntegrationAudit: 'positive',
  PaymentMethodConvertedToInvoiceAudit: 'neutral',
  RemoveMemberPermissionGroup: 'negative',
  RemovePermissionGroupRelation: 'negative',
  ResetScimTokenAudit: 'neutral',
  RestoreMembershipAudit: 'positive',
  // Eligibility adds a control; enabling the restriction itself removes uploads.
  SetRestrictedUploadsEligibilityAudit: item =>
    availabilityTone(item.restrictedUploadsEnabled),
  SetScimEnabledAudit: item => availabilityTone(item.scimEnabled),
  SetSsoConfigAudit: 'neutral',
  SetSsoEligibilityAudit: item => availabilityTone(item.ssoEnabled),
  SetWorkspaceExpiredVersionsRulesAudit: 'neutral',
  SsoMembershipProvisionedAudit: 'positive',
  SupportAccessDisabledAudit: 'negative',
  SupportAccessEnabledAudit: 'positive',
  TransferOrganizationAudit: 'neutral',
  UnarchiveProject: 'positive',
  UninstallIntegrationAudit: 'negative',
  UnknownAudit: 'neutral',
  // A null seat change says nothing about adding/removing editing access.
  // The previous role is unavailable, so role-only changes stay neutral.
  UpdateMembershipAudit: item => availabilityTone(item.changedAssignLicense),
  UpdatePermissionGroup: 'neutral',
  UpdateWorkspaceAudit: 'neutral',
}

export const toneForAuditItem = <Type extends keyof AuditItems>(
  item: AuditItems[Type]
): AuditLogTone => {
  // The typename lookup ties each classifier to its matching event payload.
  const classifier = toneClassifiers[item.__typename] as
    | AuditLogTone
    | ((item: AuditItems[Type]) => AuditLogTone)
    | undefined
  return typeof classifier === 'function'
    ? classifier(item)
    : (classifier ?? 'neutral')
}
